import { useFragment } from "react-relay"
import { t } from "i18next"
import { Tag } from "@/components/ui/tag"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { ConditionCopilots } from "@/modules/patients/detail/health-information/condition/condition-copilots"
import { ConditionsDrawer } from "@/modules/patients/detail/health-information/condition/conditions-drawer"
import { PreventionProgramsContent } from "@/modules/patients/detail/health-information/condition/prevention-programs"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useHealthInformationInContextAppointment } from "@/modules/patients/detail/health-information/hooks/useHealthInformationCanVerify"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import {
  PatientConditionInformationOutputFragment$data,
  PatientConditionInformationOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { dePatientByIdQuery$data } from "@/modules/patients/services/de/__generated__/dePatientByIdQuery.graphql"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientCondition } from "@/modules/patients/services/usePatientCondition"
import { usePatientPrevention } from "@/modules/patients/services/usePatientPrevention"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Country } from "@/types/__generated__/union-types"

export type PatientDataType = dePatientByIdQuery$data & chPatientByIdQuery$data
interface ConditionBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  country: Country
  patientId: string
}

const Condition = ({ country, patientId }: ConditionBodyProps) => {
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const appointmentId = useAppointmentParam()
  const isEditMode = useEditMode()

  const { verifiedBy, verifiedAt, isVerified } = useGetHealthInformationVerification(patientId, country, "CONDITION")

  const validAppointment = useHealthInformationInContextAppointment()
  const canVerify = !isVerified && validAppointment

  const dataRef = usePatientCondition(patientId, country)
  const data = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    dataRef,
  )
  const { conditions } = data

  const { preventionProgramItems } = usePatientPrevention(patientId, country)

  const handleOpenConditionDrawer = () => {
    openDrawer(
      () => (
        <ConditionsDrawer
          canVerify={canVerify}
          verifiedBy={verifiedBy ?? ""}
          verifiedAt={verifiedAt ?? ""}
          appointmentId={appointmentId}
          isEditMode={isEditMode}
        />
      ),
      "ConditionsDrawer",
    )
  }

  return (
    <HealthDataCardSession
      sectionName="condition"
      analyticsSectionName="healthInformation_condition"
      disabled={drawerName === "ConditionsDrawer"}
      canVerify={canVerify}
      title={t("patient.healthInformation.conditions.title")}
      editHandler={handleOpenConditionDrawer}
      bodyContentClassName="grid grid-cols-1 grid-rows-1 gap-4">
      {isEditMode && preventionProgramItems.length > 0 && (
        <PreventionProgramsContent
          isOpenConditionDrawer={false}
          patientPreventionItems={preventionProgramItems}
          closeDrawer={closeDrawer}
        />
      )}
      <ConditionCopilots
        onOpenConditionDrawer={handleOpenConditionDrawer}
        currentConditions={conditions}
        patientId={patientId}
      />
      <ConditionCardBody data={data} />
    </HealthDataCardSession>
  )
}

const ConditionCardBody = ({ data }: { data: PatientConditionInformationOutputFragment$data }) => {
  const { conditions, status } = data

  switch (status) {
    case "SOME":
      return (
        <div className="flex max-w-full flex-wrap items-center gap-2" data-testid="condition-tag-section">
          {conditions.map((condition) => (
            <Tag
              className="truncate"
              key={condition.icdDiagnosis.diagnosisCode}
              type="neutral"
              label={condition.icdDiagnosis.text}
              data-testid="condition-tag-item"
            />
          ))}
        </div>
      )
    case "NONE":
      return (
        <span
          className="text-on-surface-subdued min-h-5 px-1.5 py-1 text-xs leading-4 font-normal tracking-[0.25px]"
          data-testid="condition-none-tag">
          {t("patient.healthInformation.none")}
        </span>
      )
    case "UNKNOWN":
      return null
  }
}

export { Condition }
